// React hook：在一个 Room 里 join、收发消息、维护成员列表、持久化历史。
//
// 数据流：
//   启动 → from storage 拉历史 → setMessages
//        → 启动 WS 带 ?since=lastSeq
//        → WS 收到 message → storage.appendEvent → setMessages
//        → 发送 → 乐观 storage.appendEvent + setMessages + ws.send

import { useCallback, useEffect, useRef, useState } from "react";

import { parseQuotaFromEof, updateQuotaSnapshot } from "./quota-bus";
import { inferRelayUrl } from "./relay-url";
import { notifyRoomsChanged } from "./room-bus";
import { getStorage } from "./storage";
import type { Storage as ChatStorage, StoredEvent } from "./storage";
import { WsClient, type WsStatus } from "./ws-client";

// C1: 收/发一条消息时刷新 Room.lastMsgText/Ts。useRoom 内的消息 = 当前活跃 Room，
// 所以 unread 始终归零；多 Room 后台订阅留到 Stage 2 抽 WS 共享层时做。
async function bumpRoom(
  storage: ChatStorage,
  roomId: string,
  preview: string,
  ts: number,
): Promise<void> {
  // 现存 room（用户在某 RoomPage 里），bumpRoomActivity 内部会 read room
  // 失败时静默——比如 ID 还没 upsert 过（demo 路由首次打开 default）。
  await storage.bumpRoomActivity(roomId, preview.slice(0, 120), ts, 0, false);
  notifyRoomsChanged();
}

export interface ChatMessage {
  id: string;
  device: string;
  text: string;
  receivedAt: number;
  fromSelf?: boolean;
  /** 服务器系统通知（如 "no agent X in this room"） */
  isSystem?: boolean;
  /** agent adapter 输出（device 字段即 adapter id） */
  isAgent?: boolean;
  /** F3b: agent-host 发的审批请求，渲染成 ApprovalCard */
  approvalRequest?: {
    invocation_id: string;
    caller_user_id: string;
    room_id: string;
    adapter_id: string;
    prompt: string;
    prompt_sha256: string;
    expires_at: number;
  };
  /** D5: agent invocation 聚合 —— 同 invocation_id 的输出合并到一个 message,
   *      由 TerminalBlock 渲染。EOF 收到后 isComplete 标 true。 */
  invocationId?: string;
  isComplete?: boolean;
  /** T3 §3.4 EOF 兜底：agent-output-eof 的 reason 字段（done / cancelled /
   *  timeout_kill / agent-disconnect），UI 据此显示语义化文案。 */
  eofReason?: string;
  /** T3 §3.4 重连 catch-up：本地认为还在 running 但 agent 已经不再持有
   *  此 invocation，需要给用户"重新发起"按钮。 */
  stale?: boolean;
  /** T3 §3.5 resize ack：服务端确认的 cols/rows，TerminalBlock 比对兜底 */
  resizeAck?: { cols: number; rows: number };
  attachment?: ChatAttachment;
}

export type ChatAttachment =
  | {
      kind: "image";
      name: string;
      mime: string;
      dataUrl: string;
      size: number;
      caption?: string;
    }
  | {
      kind: "audio";
      name: string;
      mime: string;
      dataUrl: string;
      size: number;
      durationMs?: number;
      caption?: string;
    }
  | {
      kind: "html";
      name: string;
      mime: "text/html";
      dataUrl: string;
      size: number;
      caption?: string;
    };

export interface Peer {
  conn_id: number;
  device_id: string;
}

export interface UseRoomResult {
  messages: ChatMessage[];
  peers: Peer[];
  status: WsStatus;
  send: (text: string) => void;
  sendAttachment: (attachment: ChatAttachment) => void;
  /** F3b: 给 ApprovalCard 用，发任意原始 JSON 文本（如 approval-response） */
  sendRaw: (text: string) => void;
}

import { maybeNotify, textMentions } from "./notification";
import type { AuthUser } from "./auth-api";

const agentOutputDecoders = new Map<string, TextDecoder>();

export function useRoom(
  roomId: string,
  device: string,
  currentUser?: AuthUser | null,
): UseRoomResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [peers, setPeers] = useState<Peer[]>([]);
  const [status, setStatus] = useState<WsStatus>("closed");
  const clientRef = useRef<WsClient | null>(null);
  const aliveRef = useRef(true);
  // T3 §3.4: 记录"是否首次 open"，重连成功（即第 2+ 次进入 open）才触发 catch-up
  const openCountRef = useRef(0);

  useEffect(() => {
    aliveRef.current = true;
    openCountRef.current = 0;

    let client: WsClient | null = null;

    (async () => {
      const storage = await getStorage();
      const history = await storage.listEvents(roomId);
      if (!aliveRef.current) return;

      // C1: 进 Room 即清未读（用户已在看）。若 room 还没 upsert（首次打开
      // /rooms/:id 但侧栏还没建过这条），setReadCursor 找不到 room 自动 no-op。
      const maxSeq = history.reduce((m, e) => Math.max(m, e.seq), 0);
      void storage.setReadCursor(roomId, maxSeq).then(() => notifyRoomsChanged());
      // 旧 storage 里可能残留 system JSON 文本 / agent EOF 行 —— filter null
      setMessages(
        history
          .map((e) => storedToChat(e, device))
          .filter((m): m is ChatMessage => m !== null),
      );

      // 用最大 serverSeq 当 since（如果有的话）
      const maxServerSeq = history
        .map((e) => e.serverSeq ?? 0)
        .reduce((a, b) => Math.max(a, b), 0);

      const { wsUrl } = inferRelayUrl();
      client = new WsClient({
        url: wsUrl,
        room: roomId,
        device,
        // E9.7: 当前登录 user id 透传给 relay → agent-host 用作 quota 主键.
        // 已登录走 currentUser.id, 未登录场景 (anon) 给 undefined, 让 WsClient 不带这个 query.
        userId: currentUser?.id,
        since: maxServerSeq > 0 ? maxServerSeq : undefined,
        onMessage: (text) => {
          if (!aliveRef.current) return;
          handleIncoming(text, roomId, device, currentUser ?? null, setMessages, setPeers, storage);
        },
        onStatus: (s) => {
          if (!aliveRef.current) return;
          setStatus(s);
          if (s === "open") {
            openCountRef.current += 1;
            // 第 2+ 次 open == 重连成功 → 发 invocation-list-query 做 catch-up
            // backend agent-host 应答 {kind:"invocation-list", active:[...]}
            // 当前 agent-host 可能还没实现此响应；客户端发出后若 5 s 内没收到
            // 就保持本地状态不变（不主动标 stale，避免误杀）。
            if (openCountRef.current >= 2) {
              clientRef.current?.send(
                JSON.stringify({ kind: "invocation-list-query" }),
              );
            }
          }
        },
      });
      clientRef.current = client;
      client.connect();
    })().catch((err) => {
      console.error("[useRoom] init failed", err);
    });

    return () => {
      aliveRef.current = false;
      client?.close();
      clientRef.current = null;
    };
  }, [roomId, device]);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const client = clientRef.current;
      if (!client) return;

      const localBlockId = `local_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const now = Date.now();

      // 乐观本地：先入 storage 再 ws.send
      void getStorage().then(async (s) => {
        await s.appendEvent({
          seq: 0, // 由 IndexedDB autoIncrement 决定，此处占位
          blockId: localBlockId,
          serverSeq: null,
          roomId,
          ts: now,
          senderId: device,
          senderKind: "human",
          type: "text",
          body: { content: trimmed, mentions: [] },
          sig: null,
        });
        await bumpRoom(s, roomId, trimmed, now);
      });

      setMessages((prev) => [
        ...prev,
        {
          id: localBlockId,
          device,
          text: trimmed,
          receivedAt: now,
          fromSelf: true,
        },
      ]);

      client.send(trimmed);
    },
    [roomId, device],
  );

  const sendRaw = useCallback((text: string) => {
    const client = clientRef.current;
    if (!client) return;
    client.send(text);
  }, []);

  const sendAttachment = useCallback(
    (attachment: ChatAttachment) => {
      const client = clientRef.current;
      if (!client) return;
      const localBlockId = `local_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const now = Date.now();
      const wire = JSON.stringify({ kind: "chat-attachment", attachment });

      void getStorage().then(async (s) => {
        await s.appendEvent({
          seq: 0,
          blockId: localBlockId,
          serverSeq: null,
          roomId,
          ts: now,
          senderId: device,
          senderKind: "human",
          type: "attachment",
          body: { content: wire, attachment },
          sig: null,
        });
        const preview =
          attachment.caption ||
          ({
            image: "[图片]",
            audio: "[语音]",
            html: "[HTML 内容]",
          } as Record<string, string>)[attachment.kind] ||
          "[附件]";
        await bumpRoom(s, roomId, preview, now);
      });

      setMessages((prev) => [
        ...prev,
        {
          id: localBlockId,
          device,
          text: attachment.caption ?? "",
          receivedAt: now,
          fromSelf: true,
          attachment,
        },
      ]);
      client.send(wire);
    },
    [roomId, device],
  );

  return { messages, peers, status, send, sendAttachment, sendRaw };
}

// ---------- helpers ----------

interface PresencePayload {
  kind: "presence";
  event: "joined" | "left";
  conn_id: number;
  device_id: string;
}

interface SystemPayload {
  kind: "system";
  text: string;
}

function handleIncoming(
  text: string,
  roomId: string,
  selfDevice: string,
  currentUser: AuthUser | null,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setPeers: React.Dispatch<React.SetStateAction<Peer[]>>,
  storage: Awaited<ReturnType<typeof getStorage>>,
): void {
  // K8: 触发桌面通知 + 铃声 (非自己发的普通文本 / agent 输出)
  const fireNotification = (title: string, body: string) => {
    const mentionedMe = currentUser
      ? textMentions(text, {
          username: currentUser.username,
          display_name: currentUser.display_name,
          id: currentUser.id,
        })
      : false;
    void maybeNotify({ roomId, title, body, mentionedMe });
  };
  const presence = tryParsePresence(text);
  if (presence) {
    if (presence.device_id === selfDevice) return;
    if (presence.event === "joined") {
      setPeers((prev) =>
        prev.some((p) => p.conn_id === presence.conn_id)
          ? prev
          : [...prev, { conn_id: presence.conn_id, device_id: presence.device_id }],
      );
    } else {
      setPeers((prev) => prev.filter((p) => p.conn_id !== presence.conn_id));
    }
    return;
  }

  // 服务器 system 通知 —— 渲染成居中灰条，不入 storage
  // F3b: agent-host 通过 relay 转来的审批请求 JSON
  const appr = tryParseApprovalRequest(text);
  if (appr) {
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: `appr_${appr.invocation_id}`,
        device: appr.adapter_id,
        text: appr.prompt,
        receivedAt: now,
        approvalRequest: appr,
      },
    ]);
    return;
  }

  const sys = tryParseSystem(text);
  if (sys) {
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: `sys_${now.toString(36)}`,
        device: "system",
        text: sys.text,
        receivedAt: now,
        isSystem: true,
      },
    ]);
    return;
  }

  if (tryParseInternalClientProtocol(text)) {
    return;
  }

  // T3 §3.5: resize ack —— 更新对应 invocation 的 resizeAck 字段
  const ack = tryParseResizeAck(text);
  if (ack) {
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.invocationId === ack.invocationId);
      if (idx < 0) return prev;
      const copy = prev.slice();
      copy[idx] = { ...copy[idx], resizeAck: { cols: ack.cols, rows: ack.rows } };
      return copy;
    });
    return;
  }

  // T3 §3.4: invocation-list catch-up —— 不在 active 集合的本地 running 标 stale
  const list = tryParseInvocationList(text);
  if (list) {
    const activeSet = new Set(list.active);
    setMessages((prev) => {
      let changed = false;
      const next = prev.map((m) => {
        if (!m.invocationId || m.isComplete || m.stale) return m;
        if (activeSet.has(m.invocationId)) return m;
        changed = true;
        return { ...m, stale: true };
      });
      return changed ? next : prev;
    });
    return;
  }

  const agentJson = tryParseAgentOutputJson(text);
  if (agentJson) {
    if (agentJson.isEof) {
      agentOutputDecoders.get(agentJson.invocationId)?.decode();
      agentOutputDecoders.delete(agentJson.invocationId);
      void maybeNotify({
        roomId,
        title: `@${agentJson.adapter} 完成`,
        body: `# ${roomId}`,
        mentionedMe: true,
      });
    }
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.invocationId === agentJson.invocationId);
      if (idx >= 0) {
        const existing = prev[idx];
        const updated: ChatMessage = agentJson.isEof
          ? {
              ...existing,
              isComplete: true,
              eofReason: agentJson.eofReason ?? existing.eofReason,
              stale: false,
            }
          : { ...existing, text: existing.text + agentJson.text };
        const copy = prev.slice();
        copy[idx] = updated;
        return copy;
      }
      return [
        ...prev,
        {
          id: `agent_${agentJson.invocationId}`,
          device: agentJson.adapter,
          text: agentJson.text,
          receivedAt: Date.now(),
          isAgent: true,
          invocationId: agentJson.invocationId,
          isComplete: agentJson.isEof,
          eofReason: agentJson.isEof ? agentJson.eofReason : undefined,
        },
      ];
    });
    return;
  }

  const attachment = tryParseChatAttachment(text);
  if (attachment) {
    const now = Date.now();
    const remoteBlockId = `remote_${now.toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    void storage.appendEvent({
      seq: 0,
      blockId: remoteBlockId,
      serverSeq: null,
      roomId,
      ts: now,
      senderId: "peer",
      senderKind: "human",
      type: "attachment",
      body: { content: text, attachment },
      sig: null,
    });
    void bumpRoom(storage, roomId, attachmentSummary(attachment), now);
    fireNotification(`# ${roomId}`, attachmentSummary(attachment));
    setMessages((prev) => [
      ...prev,
      {
        id: remoteBlockId,
        device: "peer",
        text: attachment.caption ?? "",
        receivedAt: now,
        attachment,
      },
    ]);
    return;
  }

  // Agent adapter 输出 —— 格式 "[adapter#invocation_id] text"
  // D5: 同 invocation_id 的输出聚合到一个 TerminalBlock, 不是每行一气泡
  const agentOut = tryParseAgentOutput(text);
  if (agentOut) {
    const isEof = agentOut.text.trim() === "<EOF>";
    // K8.1: agent 完成时单独触发通知 (短铃声 tap, 不打扰但提示"任务完成")
    if (isEof) {
      void maybeNotify({
        roomId,
        title: `@${agentOut.adapter} 完成`,
        body: `# ${roomId}`,
        mentionedMe: true, // agent 输出总是发起者请求, 视为对 caller 的"提及"
      });
    }
    setMessages((prev) => {
      // 找最近的同 invocation 消息
      const idx = prev.findIndex((m) => m.invocationId === agentOut.invocationId);
      if (idx >= 0) {
        const existing = prev[idx];
        const updated: ChatMessage = isEof
          ? { ...existing, isComplete: true }
          : { ...existing, text: existing.text + agentOut.text };
        const copy = prev.slice();
        copy[idx] = updated;
        return copy;
      }
      // 第一条
      if (isEof) {
        return [
          ...prev,
          {
            id: `agent_${agentOut.invocationId}`,
            device: agentOut.adapter,
            text: "",
            receivedAt: Date.now(),
            isAgent: true,
            invocationId: agentOut.invocationId,
            isComplete: true,
          },
        ];
      }
      return [
        ...prev,
        {
          id: `agent_${agentOut.invocationId}`,
          device: agentOut.adapter,
          text: agentOut.text,
          receivedAt: Date.now(),
          isAgent: true,
          invocationId: agentOut.invocationId,
          isComplete: false,
        },
      ];
    });
    return;
  }

  const now = Date.now();
  const remoteBlockId = `remote_${now.toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

  // 写入 storage（C2 阶段消息载体是裸 Text，sender 不传；D5 接 Block 协议后改）
  void storage.appendEvent({
    seq: 0,
    blockId: remoteBlockId,
    serverSeq: null,
    roomId,
    ts: now,
    senderId: "peer",
    senderKind: "human",
    type: "text",
    body: { content: text, mentions: [] },
    sig: null,
  });

  // C1: 对端消息 → 更新 Room.lastMsg；当前正在看这个 room，所以 unread 不增。
  void bumpRoom(storage, roomId, text, now);

  // K8: 对端发的普通文本 → 触发通知
  fireNotification(`# ${roomId}`, text.length > 80 ? text.slice(0, 80) + "…" : text);

  setMessages((prev) => [
    ...prev,
    {
      id: remoteBlockId,
      device: "peer",
      text,
      receivedAt: now,
    },
  ]);
}

function tryParsePresence(text: string): PresencePayload | null {
  if (!text.startsWith("{")) return null;
  try {
    const v = JSON.parse(text);
    if (v && v.kind === "presence" && typeof v.device_id === "string") {
      return v as PresencePayload;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function tryParseApprovalRequest(text: string): {
  invocation_id: string;
  caller_user_id: string;
  room_id: string;
  adapter_id: string;
  prompt: string;
  prompt_sha256: string;
  expires_at: number;
} | null {
  if (!text.includes('"approval-request"')) return null;
  try {
    const v = JSON.parse(text);
    if (v && v.kind === "approval-request" && typeof v.invocation_id === "string") {
      return {
        invocation_id: v.invocation_id,
        caller_user_id: v.caller_user_id,
        room_id: v.room_id,
        adapter_id: v.adapter_id,
        prompt: v.prompt,
        prompt_sha256: v.prompt_sha256,
        expires_at: v.expires_at,
      };
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function tryParseSystem(text: string): SystemPayload | null {
  if (!text.startsWith("{")) return null;
  try {
    const v = JSON.parse(text);
    if (v && v.kind === "system" && typeof v.text === "string") {
      return v as SystemPayload;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function tryParseInternalClientProtocol(text: string): boolean {
  if (!text.startsWith("{")) return false;
  try {
    const v = JSON.parse(text);
    return (
      v &&
      (v.kind === "approval-response" ||
        v.kind === "terminal-input" ||
        v.kind === "terminal-resize" ||
        v.kind === "cancel-invocation" ||
        v.kind === "invocation-list-query")
    );
  } catch {
    return false;
  }
}

function tryParseAgentOutputJson(
  text: string,
): {
  adapter: string;
  invocationId: string;
  text: string;
  isEof: boolean;
  eofReason?: string;
} | null {
  if (!text.includes('"agent-output')) return null;
  try {
    const v = JSON.parse(text);
    if (
      v?.kind === "agent-output-eof" &&
      typeof v.adapter_id === "string" &&
      typeof v.invocation_id === "string"
    ) {
      // E9.9: agent-host 在 EOF 帧附带 quota snapshot (E9.8), 解析后塞进
      // quota-bus 让 RoomMembers 进度条立即刷新.
      if (v.quota) {
        const snap = parseQuotaFromEof(v.quota);
        if (snap) updateQuotaSnapshot(v.adapter_id, snap);
      }
      return {
        adapter: v.adapter_id,
        invocationId: v.invocation_id,
        text: "",
        isEof: true,
        eofReason: typeof v.reason === "string" ? v.reason : undefined,
      };
    }
    if (
      v?.kind !== "agent-output" ||
      typeof v.adapter_id !== "string" ||
      typeof v.invocation_id !== "string" ||
      typeof v.data_b64 !== "string"
    ) {
      return null;
    }
    let decoder = agentOutputDecoders.get(v.invocation_id);
    if (!decoder) {
      decoder = new TextDecoder("utf-8", { fatal: false });
      agentOutputDecoders.set(v.invocation_id, decoder);
    }
    return {
      adapter: v.adapter_id,
      invocationId: v.invocation_id,
      text: decoder.decode(base64ToBytes(v.data_b64), { stream: true }),
      isEof: false,
    };
  } catch {
    return null;
  }
}

/** T3 §3.5: agent-host 回的 resize ack（待 backend 落地，前端先解析）。 */
function tryParseResizeAck(text: string): {
  invocationId: string;
  cols: number;
  rows: number;
} | null {
  if (!text.includes('"terminal-resize-ack"')) return null;
  try {
    const v = JSON.parse(text);
    if (
      v?.kind === "terminal-resize-ack" &&
      typeof v.invocation_id === "string" &&
      typeof v.cols === "number" &&
      typeof v.rows === "number"
    ) {
      return { invocationId: v.invocation_id, cols: v.cols, rows: v.rows };
    }
  } catch {
    /* not JSON */
  }
  return null;
}

/** T3 §3.4: agent-host 应答的活跃 invocation 清单（待 backend 落地）。 */
function tryParseInvocationList(text: string): { active: string[] } | null {
  if (!text.includes('"invocation-list"')) return null;
  try {
    const v = JSON.parse(text);
    if (v?.kind === "invocation-list" && Array.isArray(v.active)) {
      const active = v.active.filter((s: unknown): s is string => typeof s === "string");
      return { active };
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function tryParseChatAttachment(text: string): ChatAttachment | null {
  if (!text.includes('"chat-attachment"')) return null;
  try {
    const v = JSON.parse(text);
    const a = v?.attachment;
    if (v?.kind !== "chat-attachment" || !a) return null;
    if (
      typeof a.kind !== "string" ||
      typeof a.name !== "string" ||
      typeof a.mime !== "string" ||
      typeof a.dataUrl !== "string" ||
      typeof a.size !== "number"
    ) {
      return null;
    }
    if (!["image", "audio", "html"].includes(a.kind)) return null;
    if (!a.dataUrl.startsWith("data:")) return null;
    return a as ChatAttachment;
  } catch {
    return null;
  }
}

function attachmentSummary(a: ChatAttachment): string {
  if (a.kind === "image") return `发送了一张图片: ${a.name}`;
  if (a.kind === "audio") return `发送了一条语音: ${a.name}`;
  return `发送了 HTML 文件: ${a.name}`;
}

// "[adapter#invocation_id] ...text..."
const AGENT_OUT_RE = /^\[([^#\]\s]+)#([^\]\s]+)\] ([\s\S]*)$/;

function tryParseAgentOutput(
  text: string,
): { adapter: string; invocationId: string; text: string } | null {
  const m = text.match(AGENT_OUT_RE);
  if (!m) return null;
  return { adapter: m[1], invocationId: m[2], text: m[3] };
}

function storedToChat(e: StoredEvent, selfDevice: string): ChatMessage | null {
  const content = extractText(e.body);

  if (tryParseInternalClientProtocol(content)) {
    return null;
  }

  const attachment = extractAttachment(e.body) ?? tryParseChatAttachment(content);
  if (attachment) {
    return {
      id: e.blockId,
      device: e.senderId,
      text: attachment.caption ?? "",
      receivedAt: e.ts,
      fromSelf: e.senderId === selfDevice,
      attachment,
    };
  }

  // 兼容旧 storage：以前 agent output / system 通知都被当普通文本写过
  const sys = tryParseSystem(content);
  if (sys) {
    return {
      id: e.blockId,
      device: "system",
      text: sys.text,
      receivedAt: e.ts,
      isSystem: true,
    };
  }

  const agentOut = tryParseAgentOutput(content);
  if (agentOut) {
    if (agentOut.text.trim() === "<EOF>") return null; // 过滤掉 EOF 占位
    return {
      id: e.blockId,
      device: agentOut.adapter,
      text: agentOut.text,
      receivedAt: e.ts,
      isAgent: true,
    };
  }

  return {
    id: e.blockId,
    device: e.senderId,
    text: content,
    receivedAt: e.ts,
    fromSelf: e.senderId === selfDevice,
  };
}

function extractText(body: unknown): string {
  if (body && typeof body === "object") {
    const c = (body as { content?: unknown }).content;
    if (typeof c === "string") return c;
  }
  return "";
}

function extractAttachment(body: unknown): ChatAttachment | null {
  if (body && typeof body === "object") {
    const a = (body as { attachment?: unknown }).attachment;
    if (a && typeof a === "object") {
      const maybe = a as Partial<ChatAttachment>;
      if (
        typeof maybe.kind === "string" &&
        typeof maybe.name === "string" &&
        typeof maybe.mime === "string" &&
        typeof maybe.dataUrl === "string" &&
        typeof maybe.size === "number"
      ) {
        return maybe as ChatAttachment;
      }
    }
  }
  return null;
}
